/**
 * `ArticleDetailPage` — generic article detail surface for `/content/:slug`
 * (TA19, Phase A PR B).
 *
 * Renders two load-bearing regions (amended 2026-05-11 per D-PD-003 —
 * the per-article demo-disclosure aside was removed during the
 * proto-parity convergence sweep; demo identity is now carried at the
 * route boundary, see `docs/governance/proto-divergences.md` D-PD-003):
 *
 *   1. The article `<h1>` (the page heading; `Layout`'s TopBar owns the
 *      shell `<h1>` only on the shell-frame element, so each route is
 *      free to declare its own page heading at level 1 — see Home.tsx
 *      file header where the same shell-vs-page heading split is in
 *      effect).
 *   2. The article body via `SanitizedHtml` (TA25). Raw
 *      `dangerouslySetInnerHTML` is forbidden anywhere outside the
 *      wrapper by the `no-unsanitised-html` architecture check
 *      (`apps/web/src/tests/architecture/no-unsanitised-html.ts`); this
 *      component honours that invariant by routing every body string
 *      through `SanitizedHtml`.
 *
 * State matrix (per `useArticle` JSDoc + the port contract):
 *
 *   - `isPending` — request in flight → render the loading skeleton
 *     (placeholder H1 + paragraph blocks). The skeleton mirrors the
 *     populated layout's vertical rhythm so the page does not jump
 *     when data arrives.
 *   - `data` is an `Article` — render H1 and body (no disclosure aside,
 *     per amended FR-018 / D-PD-003).
 *   - `data === null` — port-contract sentinel for "no row matched the
 *     slug". Render the Finnish "not found" copy. NOT an error: the
 *     port deliberately reserves `null` for the "no match" case
 *     (`ContentReaderPort.getArticleBySlug` JSDoc). Do not propagate
 *     to `PortErrorBoundary` — this is a clean state transition.
 *   - `isError` — adapter / network failure → re-throw so the
 *     caller-mounted `PortErrorBoundary` (TA22 wires the outer
 *     boundary) catches it. Rendering a custom error UI in-page would
 *     duplicate that responsibility and break the port-isolation
 *     contract that wires diagnostics through the boundary.
 *
 * Layout / above-the-fold posture:
 *
 *   The page renders inside `Layout`'s `<main>` (mobile-first column,
 *   `pt-top-bar pb-bottom-nav px-4`). The H1 uses `text-2xl font-bold`
 *   (matches Home's greeting density) so the page heading lands above
 *   the fold on a 375 × 667 viewport (after the fixed top bar) without
 *   competing with the body's lead paragraph.
 *
 * Accessibility:
 *
 *   - Single `<h1>` per page (the article title). The body's
 *     sanitised HTML may contain `<h2>`, `<h3>` etc. — DOMPurify
 *     preserves heading tags by default, so the document outline is
 *     `h1 > h2 > h3` exactly as authored.
 *   - The not-found branch renders the app's catch-all, whose heading is
 *     an `<h2>`: the shell's top bar already carries the page `<h1>`, so
 *     a second one here was the outline defect rather than the cue.
 *
 * Spec references:
 *
 *   - `specs/003-content-and-knowledge/tasks.md` TA19 (this file),
 *     TA16 (`useArticle`), TA25 (`SanitizedHtml`).
 *   - `specs/011-pwa-prototype-convergence/spec.md` FR-018 (amended
 *     2026-05-11 per D-PD-003 — demo transparency at route level),
 *     §"Content transparency" (amended 2026-05-11 per D-PD-003),
 *     FR-040 (Finnish-first copy), FR-049 (no unsanitised HTML).
 */

import type { AnalyticsPageKey } from '@omaplus/shared-types';
import type { JSX } from 'react';
import { useParams } from 'react-router';
import { AnalyticsRouteReady } from '../../analytics/AnalyticsCaptureProvider.js';
import { NotFound } from '../../routes/NotFound.js';
import type { Article } from '../ContentReaderPort.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';
import { useArticle } from '../hooks/useArticle.js';
import { ArticleContentBlocks } from './ArticleContentBlocks.js';
import {
  bodyClasses,
  DetailSurfaceShell,
  DetailSurfaceSkeleton,
  headingClasses,
} from './articleDetailSurface.js';
import { DetailSurfaceHero } from './detailSurfaceHero.js';

/**
 * Render the populated article surface (hero + H1 + sanitised body) for a
 * resolved `Article`. Shared by BOTH the injected-`article` path and the
 * fetched path so the two render through ONE composition — no duplication.
 */
function renderPopulatedSurface(article: Article, pageKey: AnalyticsPageKey): JSX.Element {
  return (
    <>
      <AnalyticsRouteReady pageKey={pageKey} />
      <DetailSurfaceShell as="article">
        <DetailSurfaceHero
          imageUrl={article.imageUrl}
          mediaLabel={article.title}
          imageAlt={article.imageAlt}
        />
        <h1 className={headingClasses}>{article.title}</h1>
        {article.contentBlocks !== undefined ? (
          <ArticleContentBlocks contentBlocks={article.contentBlocks} />
        ) : (
          <SanitizedHtml as="div" className={bodyClasses} html={article.bodyHtml} />
        )}
      </DetailSurfaceShell>
    </>
  );
}

/**
 * Props for `ArticleDetailPage`.
 *
 * - `slug` (optional): when provided, the component resolves the article
 *   from this prop instead of `useParams()`. The slug-pinned re-export
 *   routes (TA21 — `/tyosuhteen-paattaminen`, `/puhetta-palkasta`) use
 *   this path so they do not have to fake a route param via a nested
 *   router context. When the prop is absent,
 *   the component falls back to `useParams<{ slug }>()` — the original
 *   contract for the generic `/content/:slug` mount.
 *
 * Single-source-of-truth: the prop wins over the param when both
 * resolve. A consumer that wants the param-driven behaviour omits the
 * prop entirely; the prop is opt-in and never silently overridden.
 *
 * - `article` (optional): when provided, the component renders this
 *   `Article` object DIRECTLY and skips its own `useArticle` fetch (the
 *   query is disabled, so no network call happens). The US3 preview pane
 *   (T047) uses this path to render an editor's live draft through the
 *   member surface without a round-trip. Precedence is
 *   `article ?? slug ?? useParams()` — the object wins over both the
 *   slug prop and the route param. Purely additive: when absent, the
 *   existing fetch path is output-identical / behaviourally unchanged
 *   (the populated render was refactored into a shared helper — the
 *   rendered output is identical, not the source byte-for-byte).
 */
export interface ArticleDetailPageProps {
  readonly slug?: string;
  readonly article?: Article;
  /** Route-owned safe key; absent generic detail emits the canonical public content id. */
  readonly analyticsPageKey?: AnalyticsPageKey;
}

/**
 * Generic article-detail surface. See file header for the load-bearing
 * regions and state-matrix contract.
 *
 * Resolves the article slug from `props.slug` (the slug-pinned re-export
 * path used by TA21) or, when the prop is absent, from
 * `useParams<{ slug }>()` (the generic `/content/:slug` path).
 */
export function ArticleDetailPage(props: ArticleDetailPageProps): JSX.Element {
  const params = useParams<{ slug: string }>();
  // Prop wins over param. The prop path is the documented contract for
  // TA21's slug-pinned re-export routes; the param path is the generic
  // `/content/:slug` mount registered in TA22.
  const slugFromProp = props.slug;
  const slugFromParam = params.slug;

  // The route declares `:slug` (when this component is mounted under
  // `/content/:slug`); React Router resolves `params.slug` to a string
  // in that case. When the prop is provided, the param does not need to
  // be present. The empty-string fallback below is defensive: if a bug
  // at the registry mounted the component without the slug param AND
  // without the prop, the hook would dispatch a query against the empty
  // slug — the adapter MUST then surface a `null` (no row matches an
  // empty slug) and the not-found branch renders. We do not throw at
  // the seam because the JSDoc contract for `useArticle` is "given a
  // slug, dispatch the query" and a wrong-route configuration is the
  // registry's failure to catch, not this component's.
  if (
    import.meta.env.DEV &&
    props.article === undefined &&
    slugFromProp === undefined &&
    (slugFromParam === undefined || slugFromParam === '')
  ) {
    // biome-ignore lint/suspicious/noConsole: dev-only diagnostic for misconfigured route mounts
    console.error(
      'ArticleDetailPage mounted without a `slug` prop or route param. The page will render the not-found state. Check the route registry — the component expects to be mounted at `/content/:slug` or with an explicit `slug` prop.',
    );
  }
  const slug = slugFromProp ?? slugFromParam ?? '';
  // Gate the fetch: when an `article` is injected, the query is disabled so no
  // network call happens. The hook is still called UNCONDITIONALLY (Rules of
  // Hooks) — it simply does no work on the injected path.
  const query = useArticle(slug, { enabled: props.article === undefined });

  // Injected-article path (US3 preview). Resolved BEFORE the query branches so
  // the precedence `article ?? slug ?? param` holds: a provided object renders
  // the populated surface directly via the SAME render helper the fetched path
  // uses.
  if (props.article !== undefined) {
    return renderPopulatedSurface(
      props.article,
      props.analyticsPageKey ?? { kind: 'publicContent', contentId: props.article.id },
    );
  }

  if (query.isPending) {
    return <DetailSurfaceSkeleton testId="article-detail-skeleton" />;
  }

  if (query.isError) {
    // Re-throw so the caller-mounted `PortErrorBoundary` catches it.
    // `useQuery` exposes the rejection as `query.error`; if React Query
    // somehow surfaced `isError: true` with a `null` error (it does
    // not in the documented contract, but the type allows it), we
    // synthesise a generic Error so the boundary sees an Error
    // instance rather than `null`.
    throw (
      query.error ?? new Error('useArticle resolved to an error state without an Error instance')
    );
  }

  // `data === null` is the port-contract sentinel for "no row matched";
  // it is NOT a fetch error. See file header + ContentReaderPort.ts.
  if (query.data === null) {
    return <NotFound />;
  }

  // Populated branch — `data` is an `Article`. Renders through the same
  // `renderPopulatedSurface` helper as the injected path so there is one
  // populated render, not two.
  return renderPopulatedSurface(
    query.data,
    props.analyticsPageKey ?? { kind: 'publicContent', contentId: query.data.id },
  );
}
